import { getCurrentLanguage } from '@eureka/language-tool';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { GuestAwareHttpService } from '../../services/guest-aware-http-service';
import type {
    BatchItemRerunStartRequest,
    BatchItemRerunStartResponse,
    BatchTaskApi,
    BatchTaskExportRequest,
    BatchTaskProgressRequest,
    BatchTaskProgressResponse,
} from '../batch-task-api';
import { toAiccSiteLanguage } from '../batch-task-api';

/** 使用交接文档定义的真实批量 CC 接口。 */
@injectable()
export class BatchTaskApiImpl extends GuestAwareHttpService implements BatchTaskApi {
    getBatchProgress(
        request: BatchTaskProgressRequest
    ): Promise<BatchTaskProgressResponse> {
        return this.getWithGuestSupport<BatchTaskProgressResponse>(
            'batch/progress',
            { batch_task_id: request.batch_task_id },
            this.getBatchLanguageHeader()
        );
    }

    /**
     * 先通知后端重置现有子任务，再由页面使用同一个 child_task_id 进入单条流程。
     * 这里刻意不调用 /task/init，避免生成新任务并切断批次与子任务的关联。
     */
    startBatchItemRerun(
        request: BatchItemRerunStartRequest
    ): Promise<BatchItemRerunStartResponse> {
        return this.postWithGuestSupport<BatchItemRerunStartResponse>(
            {
                data: request,
                headers: this.getBatchLanguageHeader(),
            },
            'batch/item/rerun/start'
        );
    }

    async exportBatchResults(request: BatchTaskExportRequest): Promise<Blob> {
        const resolvedUrl = await this.resolveApiPath('batch/export');
        const batchTaskId = encodeURIComponent(request.batch_task_id);

        return this.get({
            url: `${resolvedUrl}?batch_task_id=${batchTaskId}`,
            headers: {
                ...this.getDefaultHeaders(),
                ...this.getBatchLanguageHeader(),
            },
            responseType: 'blob',
        }) as unknown as Promise<Blob>;
    }

    private getBatchLanguageHeader(): Record<string, string> {
        return {
            'x-site-lang': toAiccSiteLanguage(getCurrentLanguage()),
        };
    }

    /** 路径解析继续复用项目已有的登录/游客分流规则。 */
    private async resolveApiPath(basePath: string): Promise<string> {
        const { resolveApiPath } = await import('@/utils/api-path-resolver');
        return resolveApiPath(basePath);
    }
}
